"use client";

import Link from "next/link";
import { Lock } from "lucide-react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import type { Locale } from "@/lib/i18n";

/**
 * The Pro card inside one locked problem (`LN-ACC01`, docs/35 §2 and §2.1).
 *
 * A problems page holds free and Pro problems side by side, so a Pro problem is
 * locked inside its own card rather than the page being locked: its statement,
 * sample and two hints stay where they are, and this card sits after the last
 * hint, where the solution button would be. It says what is held back and
 * offers the same two ways in as the Pro lesson card (`LearnLockCard`, the
 * track and Learn Pro), with the sign-in line for a reader who is signed out
 * and may already hold one (`LN-LCH01`). No countdown, no blur: the server
 * never sent the solution, so there is nothing here to hide.
 *
 * It is a component, mounted by a portal into the node the hint ladder leaves
 * in the stored HTML, so its buttons are the `Button` primitive (`AGENTS.md`
 * rule 17) and not injected DOM.
 */
export function LearnProblemLock({
  locale,
  trackSlug,
  signInHref,
}: {
  locale: Locale;
  trackSlug: string;
  /** Present only for a signed-out reader. */
  signInHref: string | null;
}) {
  const t = useTranslations("learn.reader");
  return (
    <div
      className="my-4 rounded-[var(--radius)] border border-orange-200 bg-orange-50 p-4"
      data-testid="problem-lock"
    >
      <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em] text-primary-ink">
        <Lock aria-hidden className="h-3.5 w-3.5" />
        {t("problemLockEyebrow")}
      </p>
      <p className="mt-1.5 text-[14.5px] leading-relaxed text-neutral-700">{t("problemLockBody")}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button asChild size="sm">
          <Link href={`/${locale}/learn/${trackSlug}`}>{t("lockBuyTrack")}</Link>
        </Button>
        <Button asChild size="sm" variant="secondary">
          <Link href={`/${locale}/learn/pro`}>{t("lockGetPro")}</Link>
        </Button>
      </div>
      {signInHref ? (
        <p className="mt-2.5 text-[13.5px] text-neutral-600">
          <Link className="font-medium text-primary-ink underline hover:no-underline" href={signInHref}>
            {t("lockProSignIn")}
          </Link>
        </p>
      ) : null}
    </div>
  );
}
